<template>
  <div class="internal-bady store-planners">
    <!-- Sticky Header -->
    <NavBar />
    <div id="top"></div>

    <main class="store-planners-main" dir="rtl" lang="he" :aria-label="t('storeForPlannersMetaTitle')">
      <div class="px-3 px-md-4" id="welcome-section">
        <div :style="heroStyle" class="container-fluid bg-welcome rounded-5 d-flex align-items-center">
          <div class="container position-index">
            <div class="row justify-content-center">
              <div class="col-12 col-lg-10 col-xl-8 d-flex flex-column gap-3 gap-md-5">
                <h1 id="hero-title" class="text-welcome text-white mb-0 text-center">
                  {{ t('storeForPlannersHeroTitle') }}
                </h1>
                <div class="hero-body text-white">
                  <p class="hero-lead">
                    {{ t('storeForPlannersHeroLead') }}
                  </p>
                  <p class="mb-3">
                    {{ t('storeForPlannersHeroTwoWays') }}
                  </p>
                  <ul class="hero-list mb-3">
                    <li>{{ t('storeForPlannersCustomItineraries') }}</li>
                    <li>{{ t('storeForPlannersShelfProduct') }}</li>
                  </ul>
                  <p class="mb-3">
                    {{ t('storeForPlannersHeroAlsoSell') }}
                  </p>
                  <p class="mb-0 hero-disclaimer">
                    {{ t('storeForPlannersHeroDisclaimer') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- What Customer Gets -->
      <section class="customer-gets" aria-labelledby="customer-gets-title">
        <h2 id="customer-gets-title" class="section__title">{{ t('storeForPlannersWhatClientGets') }}</h2>
        <div class="customer-gets__highlight-box">
          <p class="customer-gets__emphasis">
            <span v-html="t('storeForPlannersClientEmphasis')"></span>
          </p>
        </div>
        <ul class="customer-gets__list">
          <li class="customer-gets__item" v-for="(feature, index) in customerFeatures" :key="index">
            <span class="customer-gets__icon" aria-hidden="true">✔</span>
            <span class="customer-gets__text">{{ feature }}</span>
          </li>
        </ul>
        <p class="customer-gets__conclusion">
          {{ t('storeForPlannersClientConclusion') }}
        </p>
        <p class="customer-gets__conclusion customer-gets__conclusion--secondary">
          {{ t('storeForPlannersClientConclusionSecondary') }}
        </p>
      </section>

      <!-- Screenshots Carousel -->
      <section class="showcase" aria-labelledby="showcase-title">
        <h2 id="showcase-title" class="section__title">{{ t('storeForPlannersHowItLooksInPractice') }}</h2>
        <p class="showcase__subtitle">{{ t('storeForPlannersAllInOneClear') }}</p>
        
        <div class="carousel" dir="ltr">
          <div class="carousel__container">
            <div class="carousel__track" :style="{ transform: `translateX(${currentSlide * 100}%)` }">
              <div 
                v-for="(screenshot, index) in screenshots" 
                :key="index"
                class="carousel__slide"
              >
                <div class="phone-mockup">
                  <img 
                    :src="screenshot.url" 
                    :alt="screenshot.caption"
                    class="phone-mockup__screen"
                    loading="lazy"
                  />
                </div>
                <p class="carousel__caption" dir="rtl">{{ screenshot.caption }}</p>
              </div>
            </div>
          </div>

          <!-- Navigation Arrows -->
          <button 
            class="carousel__btn carousel__btn--prev" 
            @click="prevSlide"
            :disabled="currentSlide === 0"
            :aria-label="t('storeForPlannersPrevImage')"
          >
            ❮
          </button>
          <button 
            class="carousel__btn carousel__btn--next" 
            @click="nextSlide"
            :disabled="currentSlide === -(screenshots.length - 1)"
            :aria-label="t('storeForPlannersNextImage')"
          >
            ❯
          </button>

          <!-- Dots Indicator -->
          <div class="carousel__dots">
            <button
              v-for="(screenshot, index) in screenshots"
              :key="`dot-${index}`"
              class="carousel__dot"
              :class="{ 'carousel__dot--active': currentSlide === -index }"
              @click="goToSlide(index)"
              :aria-label="t('storeForPlannersSlideLabel', { slideNumber: index + 1 })"
            ></button>
          </div>
        </div>
      </section>

      <!-- What is the Store -->
      <section class="intro" aria-labelledby="intro-title">
        <h2 id="intro-title" class="section__title">{{ t('storeForPlannersWhatIsStore') }}</h2>
        <p class="intro__text">
          {{ t('storeForPlannersIntroText1') }}
        </p>
        <p class="intro__text">
          {{ t('storeForPlannersIntroText2') }}
        </p>
        <p class="intro__text intro__text--highlight">
          {{ t('storeForPlannersIntroText3') }}
        </p>
      </section>

      <!-- How It Works -->
      <section class="how-it-works" aria-labelledby="how-it-works-title">
        <h2 id="how-it-works-title" class="section__title">{{ t('storeForPlannersHowToSell') }}</h2>
        <div class="steps">
          <div class="step" v-for="(step, index) in steps" :key="index">
            <div class="step__number">{{ index + 1 }}</div>
            <div class="step__content">
              <h3 class="step__title">{{ step.title }}</h3>
              <p class="step__description" v-if="step.description">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Benefits -->
      <section class="benefits" aria-labelledby="benefits-title">
        <h2 id="benefits-title" class="section__title">
          {{ t('storeForPlannersBenefitsCentralTitle') }}
        </h2>
        <ul class="benefits__list">
          <li class="benefits__item" v-for="(benefit, index) in benefits" :key="index">
            <span class="benefits__icon" aria-hidden="true">✔</span>
            <span class="benefits__text">
              <strong>{{ benefit.title }}</strong>
              {{ benefit.description }}
            </span>
          </li>
        </ul>
      </section>

      <!-- Demo -->
      <section class="demo" aria-labelledby="demo-title">
        <h2 id="demo-title" class="section__title">{{ t('storeForPlannersHowItLooksInPractice') }}</h2>
        
        <!-- Shop Example -->
        <div class="demo__example">
          <h3 class="demo__example-title">{{ t('storeForPlannersSampleClientItinerary') }}</h3>
          <div class="demo__link-container">
            <a :href="productDemoLink" target="_blank" rel="noopener" class="demo__link">
              {{ productDemoLink }}
            </a>
          </div>
        </div>

        <!-- Product Example -->
        <div class="demo__example">
          <h3 class="demo__example-title">{{ t('storeForPlannersSampleStore') }}</h3>
          <div class="demo__link-container">
            <a :href="shopDemoLink" target="_blank" rel="noopener" class="demo__link">
              {{ shopDemoLink }}
            </a>
          </div>
        </div>
      </section>

      <!-- Contact Form -->
      <section class="contact-form-section" id="contact-form" aria-labelledby="contact-form-title">
        <h2 id="contact-form-title" class="section__title">{{ t('storeForPlannersContactTitle') }}</h2>
        <p class="contact-form__description">
          {{ t('storeForPlannersContactSubtitle') }}
        </p>
        <p class="contact-form__description">
          {{ t('storeForPlannersContactFormAudience') }}
        </p>
        <form @submit.prevent="handleContactSubmit" class="contact-form">
          <div class="contact-form__field">
            <b-form-input
              v-model="contactFormData.name"
              :placeholder="t('storeForPlannersFullName')"
              class="contact-form__input"
              size="lg"
              required
            />
          </div>
          <div class="contact-form__field">
            <b-form-input
              v-model="contactFormData.phone"
              type="tel"
              :placeholder="t('storeForPlannersPhonePlaceholder')"
              class="contact-form__input"
              size="lg"
              required
              autocomplete="tel"
            />
          </div>
          <div class="contact-form__field">
            <b-form-input
              v-model="contactFormData.email"
              type="email"
              :placeholder="t('storeForPlannersEmailPlaceholder')"
              class="contact-form__input"
              size="lg"
            />
          </div>
          <div class="contact-form__field">
            <b-form-textarea
              v-model="contactFormData.message"
              :placeholder="t('storeForPlannersMessagePlaceholder')"
              class="contact-form__textarea"
              rows="4"
            />
          </div>
          <div class="contact-form__field contact-form__field--checkbox">
            <label class="contact-form__checkbox-label">
              <input
                type="checkbox"
                v-model="contactFormData.privacyPolicyAccepted"
                class="contact-form__checkbox-input"
                required
              />
              <span class="contact-form__checkbox-text">{{ t('storeForPlannersReadAndApprove') }} </span>
              <router-link to="/privacy_policy" target="_blank" class="contact-form__privacy-link" @click.stop>{{ t('privacyPolicy') }}</router-link>
            </label>
          </div>
          <div class="contact-form__submit">
            <b-button
              type="submit"
              class="contact-form__button"
              :disabled="contactFormLoading"
            >
              <span v-if="contactFormLoading" class="spinner-border spinner-border-sm mr-2"></span>
              {{ contactFormLoading ? t('storeForPlannersSending') : t('storeForPlannersSendDetails') }}
            </b-button>
          </div>
          <div v-if="contactFormMessage" class="contact-form__message" :class="contactFormMessageType">
            {{ contactFormMessage }}
          </div>
        </form>
      </section>

      <!-- CTA -->
      <section class="cta" aria-labelledby="cta-title">
        <h2 id="cta-title" class="section__title">{{ t('storeForPlannersWantToSeeFit') }}</h2>
        <p class="cta__lead">
          {{ t('storeForPlannersCtaLead1') }}
        </p>
        <p class="cta__lead">
          {{ t('storeForPlannersCtaLead2') }}
        </p>
        <div class="cta__buttons">
          <button
            class="cta__button"
            type="button"
            :aria-label="t('storeForPlannersWhatsAppCta')"
            @click="openContact"
          >
            <svg class="cta__whatsapp-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            {{ t('storeForPlannersWhatsAppCta') }}
          </button>
          <button
            class="cta__button cta__button--secondary"
            type="button"
            :aria-label="t('storeForPlannersLeaveDetails')"
            @click="openContactForm"
          >
            {{ t('storeForPlannersLeaveDetails') }}
          </button>
        </div>
      </section>
    </main>

    <!-- Minimal Footer -->
    <PageFooterNew />
  </div>
</template>

<script>
import contactApi from '@/api/contact';
import { isValidContactPhone } from '@/common/validateContactPhone';
import NavBar from '@/components/NavBar.vue'
import PageFooterNew from '@/components/PageFooterNew.vue'
import { translationMixin } from '@/mixins/translationMixin';

export default {
  name: 'StoreForPlanners',
  mixins: [translationMixin],
  components: {
    NavBar,
    PageFooterNew
  },
  data() {
    return {
      heroStyle: {
        '--home-bg-image': `url('https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/page_images%2Fwelcome-trip.jpg?alt=media&token=0454fe77-65ec-4ece-8c99-6d978d2a7bbd')`
      },
      previousHtmlFontSize: '',
      contactFormData: {
        name: '',
        email: '',
        phone: '',
        message: '',
        source: 'store_for_planners',
        privacyPolicyAccepted: false
      },
      contactFormLoading: false,
      contactFormMessage: '',
      contactFormMessageType: '',
      currentSlide: 0,
    }
  },
  props: {
    /* העדיפות: calendarUrl > whatsappNumber > tel > email */
    calendarUrl: { type: String, default: '' },
    whatsappNumber: { type: String, default: '972526867915' },
    whatsappMessage: { type: String, default: '' },
    tel: { type: String, default: '' },
    email: { type: String, default: '' },
    shopDemoLink: {
      type: String,
      default: 'https://mayatours.tripplanner.co.il/featured-rides/tripplanner'
    },
    productDemoLink: {
      type: String,
      default: 'https://tripplanner.co.il/single_ride_with_map/6095'
    }
  },
  computed: {
    customerFeatures() {
      return [
        this.t('storeForPlannersFeatureDailyClear'),
        this.t('storeForPlannersFeatureMap'),
        this.t('storeForPlannersFeatureNav'),
        this.t('storeForPlannersFeatureNoApp'),
      ];
    },
    steps() {
      return [
        { title: this.t('storeForPlannersStepCreate'), description: this.t('storeForPlannersStepCreateBody') },
        { title: this.t('storeForPlannersStepPublish'), description: this.t('storeForPlannersStepPublishBody') },
        { title: this.t('storeForPlannersStepClientAccess'), description: this.t('storeForPlannersStepClientAccessBody') },
      ];
    },
    benefits() {
      return [
        { title: this.t('storeForPlannersBenefitDigital'), description: this.t('storeForPlannersBenefitDigitalBody') },
        { title: this.t('storeForPlannersBenefitReusable'), description: this.t('storeForPlannersBenefitReusableBody') },
        { title: this.t('storeForPlannersBenefitClearExp'), description: this.t('storeForPlannersBenefitClearExpBody') },
        { title: this.t('storeForPlannersBenefitNoHassle'), description: this.t('storeForPlannersBenefitNoHassleBody') },
        { title: this.t('storeForPlannersBenefitYouControl'), description: this.t('storeForPlannersBenefitYouControlBody') },
      ];
    },
    screenshots() {
      return [
        { url: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_ride_sumery.png?alt=media&token=6ac83bff-0d6d-4f87-a12d-b79fcaf20bf2', caption: this.t('storeForPlannersScreenshot1') },
        { url: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_day_at_the_ride.png?alt=media&token=6a746668-6178-404b-9c45-1dc4b982e38e', caption: this.t('storeForPlannersScreenshot2') },
        { url: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_ride_atraction_summery.png?alt=media&token=cbfde260-cda4-4471-8266-b524739239f5', caption: this.t('storeForPlannersScreenshot3') },
        { url: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_prisenting%20_the_map.png?alt=media&token=4cb13652-f90c-4c2a-b067-a8b69066aae7', caption: this.t('storeForPlannersScreenshot4') },
        { url: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_nevigate_to_destination.png?alt=media&token=f58fb2cd-3266-47c0-8a57-f3a851614762', caption: this.t('storeForPlannersScreenshot5') },
      ];
    },
    effectiveWhatsAppMessage() {
      return this.whatsappMessage || this.t('storeForPlannersWhatsAppMessage');
    },
  },
  methods: {
    openContact() {
      // 1) אם יש יומן/קישור קביעת פגישה - נפתח אותו
      if (this.calendarUrl) {
        window.open(this.calendarUrl, '_blank', 'noopener');
        return;
      }
      // 2) אם יש וואטסאפ - נפתח הודעה מוכנה
      if (this.whatsappNumber) {
        const msg = encodeURIComponent(this.effectiveWhatsAppMessage);
        const url = `https://wa.me/${this.whatsappNumber}?text=${msg}`;
        window.open(url, '_blank', 'noopener');
        return;
      }
      // 3) טלפון
      if (this.tel) {
        window.location.href = `tel:${this.tel}`;
        return;
      }
      // 4) אימייל
      if (this.email) {
        const subject = encodeURIComponent(this.t('storeForPlannersMeetingRequestSubject'));
        const body = encodeURIComponent(this.effectiveWhatsAppMessage);
        window.location.href = `mailto:${this.email}?subject=${subject}&body=${body}`;
        return;
      }
      // 5) Fallback - במקרה ואין שום דבר, נפתח טופס יצירת קשר
      this.openContactForm();
    },
    openContactForm() {
      // Scroll to the contact form section
      const formElement = document.getElementById('contact-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    prevSlide() {
      if (this.currentSlide < 0) {
        this.currentSlide++;
      }
    },
    nextSlide() {
      if (this.currentSlide > -(this.screenshots.length - 1)) {
        this.currentSlide--;
      }
    },
    goToSlide(index) {
      this.currentSlide = -index;
    },
    async handleContactSubmit() {
      if (!this.validateContactForm()) {
        return;
      }
      
      this.contactFormLoading = true;
      this.contactFormMessage = '';
      
      try {
        const success = await contactApi.submitContactForm(this.contactFormData);
        
        if (success) {
          this.contactFormMessage = this.t('storeForPlannersContactSuccess');
          this.contactFormMessageType = 'success';
          this.resetContactForm();
        }
      } catch (error) {
        console.error('Contact form submission error:', error);
        this.contactFormMessage = this.t('storeForPlannersContactError');
        this.contactFormMessageType = 'error';
      } finally {
        this.contactFormLoading = false;
      }
    },
    validateContactForm() {
      if (!this.contactFormData.name.trim()) {
        this.contactFormMessage = this.t('storeForPlannersValidateName');
        this.contactFormMessageType = 'error';
        return false;
      }

      if (!this.contactFormData.phone.trim()) {
        this.contactFormMessage = this.t('storeForPlannersValidatePhone');
        this.contactFormMessageType = 'error';
        return false;
      }
      if (!isValidContactPhone(this.contactFormData.phone)) {
        this.contactFormMessage = this.t('storeForPlannersValidatePhoneFormat');
        this.contactFormMessageType = 'error';
        return false;
      }

      // Validate email only if provided
      if (this.contactFormData.email.trim() && !this.validateEmail(this.contactFormData.email)) {
        return false;
      }

      // Validate privacy policy acceptance
      if (!this.contactFormData.privacyPolicyAccepted) {
        this.contactFormMessage = this.t('storeForPlannersValidatePrivacy');
        this.contactFormMessageType = 'error';
        return false;
      }
      
      return true;
    },
    validateEmail(email) {
      const trimmedEmail = email.trim();
      
      if (trimmedEmail.includes(' ')) {
        this.contactFormMessage = this.t('storeForPlannersValidateEmailSpaces');
        this.contactFormMessageType = 'error';
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        this.contactFormMessage = this.t('storeForPlannersValidateEmailFormat');
        this.contactFormMessageType = 'error';
        return false;
      }
      
      return true;
    },
    resetContactForm() {
      this.contactFormData = {
        name: '',
        email: '',
        phone: '',
        message: '',
        source: 'store_for_planners',
        privacyPolicyAccepted: false
      };
    },
  }
  ,
  mounted() {
    try {
      this.previousHtmlFontSize = document.documentElement.style.fontSize || ''
      document.documentElement.style.fontSize = '100%'
    } catch (e) {
      // ignore
    }
  },
  beforeDestroy() {
    try {
      document.documentElement.style.fontSize = this.previousHtmlFontSize || ''
    } catch (e) {
      // ignore
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.internal-bady {
  font-family: "Noto Sans Hebrew", "Rubik", "Nunito", sans-serif;
  color: #1e1e1e;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.store-planners-main {
  background: #fff;
}

#welcome-section {
  padding-top: 98px;
  padding-bottom: 24px;
  height: 100dvh;
}

.text-welcome {
  font-size: 72px;
  font-weight: 800;
  text-shadow: 0 4px 18px rgba(0, 0, 0, 0.45);
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}

.hero-note {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.95;
}

.hero-lead {
  font-weight: 800;
  font-size: clamp(1.05rem, 0.95rem + 0.4vw, 1.25rem);
  line-height: 1.45;
  margin-bottom: 14px;
}

.hero-highlight {
  display: inline-flex;
  align-self: center;
  padding: 12px 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: rgba(255, 255, 255, 0.98);
  font-weight: 800;
}

.hero-description {
  max-width: 920px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.95;
}

.hero-body {
  max-width: 860px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  line-height: 1.75;
  padding: 18px 22px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
}

.hero-list {
  display: inline-block;
  text-align: right;
  padding-right: 22px;
  margin: 0;
}

.hero-list li {
  margin: 8px 0;
}

.hero-disclaimer {
  opacity: 0.95;
}

.hero-body p {
  margin: 0 0 12px;
}

.hero-body p:last-child {
  margin-bottom: 0;
}

.bg-welcome {
  position: relative;
  overflow: hidden;
  height: 100%;
}

.bg-welcome::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: var(--home-bg-image);
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 32px;
  z-index: 0;
}

.bg-welcome::after {
  position: absolute;
  content: '';
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 32px;
  background:
    linear-gradient(0deg, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)),
    linear-gradient(45deg, rgba(43, 43, 43, 0), rgba(30, 30, 30, 0.8));
  z-index: 1;
}

.position-index {
  position: relative;
  z-index: 2;
}

.rounded-5 {
  border-radius: 32px !important;
}

.text-primary {
  background-image: linear-gradient(45deg, #34D0B6, #07A0C3) !important;
  color: transparent !important;
  -webkit-background-clip: text !important;
  -moz-background-clip: text !important;
  background-clip: text !important;
}

.text-secondary {
  color: #0D2F29 !important;
}

.btn-primary {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  border: 0;
}

.btn-primary:hover {
  background: linear-gradient(45deg, #39e1c5, #08add3);
  color: white;
  border: 0;
}

@media (max-width: 767px) {
  #welcome-section {
    padding-top: 74px;
    padding-bottom: 16px;
    height: auto;
    min-height: 100dvh;
  }

  #welcome-section .bg-welcome {
    height: auto;
    min-height: 100dvh;
    align-items: flex-start !important;
  }

  #welcome-section .position-index {
    padding-top: 18px;
    padding-bottom: 18px;
  }

  .text-welcome {
    font-size: 34px;
    font-weight: 800;
    line-height: 1.1;
  }

  .hero-body {
    padding: 14px 14px;
  }
}

.store-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Fixed Header */
.store-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
}

.store-header__container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.store-header__logo {
  display: flex;
  align-items: center;
}

.store-header__logo img {
  height: 32px;
  width: auto;
}

.store-header__cta {
  background: linear-gradient(135deg, #40C4C2 0%, #2A9D9B 100%);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(64, 196, 194, 0.4);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}

.store-header__whatsapp-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.store-header__cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(64, 196, 194, 0.6);
}

.store-header__cta:active {
  transform: translateY(0);
}

/* Main Store Content */
.store {
  background: linear-gradient(135deg, #40C4C2 0%, #2A9D9B 100%);
  flex: 1;
  padding: 0;
  padding-top: 56px;
  color: #ffffff;
  line-height: 1.7;
  font-size: 16px;
  position: relative;
  overflow-x: hidden;
}

.store::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
  pointer-events: none;
}

.store > section {
  position: relative;
  z-index: 1;
}

.section__title {
  font-size: 22px;
  font-weight: 800;
  margin: 24px 0 16px;
  color: #fff;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  padding: 0 10px;
  text-align: center;
}

/* Hero Section */
.hero {
  text-align: center;
  padding: 40px 20px 70px;
  background: linear-gradient(135deg, rgba(64, 196, 194, 0.9) 0%, rgba(42, 157, 155, 0.9) 100%);
  margin: 0;
  position: relative;
  overflow: hidden;
}

.hero__logo {
  margin-bottom: 24px;
  animation: fadeInDown 0.8s ease-out;
  position: relative;
  z-index: 2;
}

.hero__logo img {
  height: 60px;
  width: auto;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
}

.hero::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(135deg, #40C4C2 0%, #2A9D9B 100%);
  clip-path: polygon(0 50%, 100% 0, 100% 100%, 0% 100%);
  z-index: 1;
}

.hero__title {
  font-size: 28px;
  font-weight: 900;
  margin: 0 0 16px;
  background: linear-gradient(135deg, #fff 0%, #f0f0ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: fadeInUp 0.8s ease-out;
  line-height: 1.3;
  padding: 0 10px;
  position: relative;
  z-index: 2;
}

.hero__subtitle {
  font-size: 16px;
  margin: 0 auto 20px;
  color: rgba(255, 255, 255, 0.95);
  max-width: 800px;
  line-height: 1.6;
  animation: fadeInUp 1s ease-out;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 0 10px;
  position: relative;
  z-index: 2;
}

.hero__note {
  font-size: 17px;
  margin: 0 auto 20px;
  color: rgba(255, 255, 255, 0.95);
  max-width: 800px;
  line-height: 1.6;
  animation: fadeInUp 1.1s ease-out;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 2;
  font-weight: 600;
}

.hero__highlight {
  margin: 30px auto;
  max-width: 700px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  position: relative;
  z-index: 2;
  animation: fadeInUp 1.2s ease-out;
}

.hero__highlight-text {
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  margin: 0;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.hero__description {
  font-size: 16px;
  margin: 20px auto 0;
  color: rgba(255, 255, 255, 0.95);
  max-width: 800px;
  line-height: 1.7;
  animation: fadeInUp 1.4s ease-out;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 0 10px;
  position: relative;
  z-index: 2;
}

.hero__cta {
  margin: 32px auto 0;
  text-align: center;
  position: relative;
  z-index: 2;
  animation: fadeInUp 1.6s ease-out;
}

.hero__whatsapp-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  color: #2A9D9B;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  padding: 14px 32px;
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.hero__whatsapp-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  background: #fff;
}

.hero__whatsapp-button:active {
  transform: translateY(-1px);
}

.section__cta {
  margin: 32px auto 0;
  text-align: center;
  padding: 0 20px;
}

.section__whatsapp-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #40C4C2 0%, #2A9D9B 100%);
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(64, 196, 194, 0.4);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.section__whatsapp-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(64, 196, 194, 0.6);
}

.section__whatsapp-button:active {
  transform: translateY(-1px);
}

/* Customer Gets Section */
.customer-gets {
  max-width: 900px;
  margin: 40px auto;
  padding: 40px 20px;
  text-align: center;
}

.customer-gets__highlight-box {
  margin: 24px auto;
  max-width: 800px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.customer-gets__emphasis {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  margin: 0;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.customer-gets__list {
  list-style: none;
  margin: 32px auto;
  padding: 0;
  max-width: 700px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.customer-gets__item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: right;
}

.customer-gets__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #40C4C2 0%, #5FE9E7 100%);
  border-radius: 50%;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(64, 196, 194, 0.5);
  font-size: 14px;
  color: #fff;
}

.customer-gets__text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 16px;
  line-height: 1.6;
  flex: 1;
}

.customer-gets__conclusion {
  margin: 24px auto 0;
  font-size: 17px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.7;
  max-width: 800px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.customer-gets__conclusion--secondary {
  margin-top: 12px;
  font-weight: 600;
}

/* Intro Section */
.intro {
  max-width: 900px;
  margin: 0 auto;
  padding: 56px 20px;
  text-align: center;
}

.intro__text {
  margin: 12px 0;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.7;
}

.intro__flow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  margin: 16px auto 0;
  text-align: center;
}

.intro__flow-segment {
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  line-height: 1.6;
}

.intro__flow-arrow {
  font-size: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  line-height: 1;
  vertical-align: middle;
  padding: 0 4px;
  position: relative;
  top: -1px;
}

.intro__text--highlight {
  font-weight: 700;
  font-size: 18px;
  margin-top: 20px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* How It Works Section */
.how-it-works {
  max-width: 1000px;
  margin: 40px auto;
  padding: 40px 20px;
}

.steps {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 32px;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  animation: fadeInUp 0.6s ease-out backwards;
}

.step:nth-child(1) { animation-delay: 0.1s; }
.step:nth-child(2) { animation-delay: 0.2s; }
.step:nth-child(3) { animation-delay: 0.3s; }
.step:nth-child(4) { animation-delay: 0.4s; }
.step:nth-child(5) { animation-delay: 0.5s; }

.step:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

.step__number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #40C4C2 0%, #5FE9E7 100%);
  border-radius: 50%;
  font-weight: 800;
  font-size: 20px;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(64, 196, 194, 0.5);
}

.step__content {
  flex: 1;
}

.step__title {
  font-size: 18px;
  font-weight: 800;
  margin: 0 0 8px;
  color: #fff;
}

.step__description {
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.steps__note {
  margin-top: 32px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  font-size: 16px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.7;
  text-align: center;
  white-space: pre-line;
}

/* Benefits Section */
.benefits {
  max-width: 1000px;
  margin: 0 20px;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

.benefits__list {
  list-style: none;
  margin: 24px 0 0;
  padding: 0;
  display: grid;
  gap: 16px;
}

.benefits__item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  animation: fadeInUp 0.6s ease-out backwards;
}

.benefits__item:nth-child(1) { animation-delay: 0.1s; }
.benefits__item:nth-child(2) { animation-delay: 0.2s; }
.benefits__item:nth-child(3) { animation-delay: 0.3s; }
.benefits__item:nth-child(4) { animation-delay: 0.4s; }
.benefits__item:nth-child(5) { animation-delay: 0.5s; }
.benefits__item:nth-child(6) { animation-delay: 0.6s; }

.benefits__item:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

.benefits__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #40C4C2 0%, #5FE9E7 100%);
  border-radius: 50%;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
  box-shadow: 0 4px 12px rgba(64, 196, 194, 0.5);
  font-size: 14px;
}

.benefits__text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 15px;
  line-height: 1.6;
}

.benefits__text strong {
  font-weight: 800;
  color: #fff;
  display: block;
  margin-bottom: 4px;
  font-size: 16px;
}

/* Pricing Section */
.pricing {
  max-width: 1000px;
  margin: 40px auto;
  padding: 40px 20px;
  text-align: center;
}

.pricing__simple {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 32px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.pricing__simple-item {
  padding: 24px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  font-size: 18px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  transition: all 0.3s ease;
}

.pricing__simple-item:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.18);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

/* Demo Section */
.demo {
  max-width: 900px;
  margin: 40px auto;
  padding: 32px 20px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  text-align: center;
}

.demo__example {
  margin-bottom: 40px;
}

.demo__example:last-child {
  margin-bottom: 0;
}

.demo__example-title {
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 12px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.demo__example-description {
  margin: 0 0 20px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.95);
  padding: 0 10px;
}

.demo__link-container {
  margin: 0 auto;
  max-width: 100%;
  padding: 16px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.demo__link {
  color: #2A9D9B;
  font-weight: 700;
  text-decoration: none;
  font-size: 15px;
  word-break: break-all;
  transition: color 0.3s ease;
  display: block;
}

.demo__link:hover {
  color: #40C4C2;
  text-decoration: underline;
}

/* Screenshots Carousel Section */
.showcase {
  max-width: 1200px;
  margin: 28px auto;
  padding: 28px 12px;
  text-align: center;
}

.showcase__subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  margin: 10px 0 22px;
  padding: 0 10px;
}

.carousel {
  position: relative;
  max-width: 380px;
  margin: 0 auto;
  padding: 0 40px;
}

.carousel__container {
  overflow: hidden;
  border-radius: 24px;
  direction: ltr;
  width: 100%;
}

.carousel__track {
  display: flex;
  transition: transform 0.5s ease-in-out;
  direction: ltr;
  width: 100%;
}

.carousel__slide {
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px;
  box-sizing: border-box;
}

/* Phone Mockup */
.phone-mockup {
  position: relative;
  width: 100%;
  max-width: 235px;
  margin: 0 auto;
  background: #1a1a1a;
  border-radius: 32px;
  padding: 8px;
  box-shadow: 
    0 15px 40px rgba(0, 0, 0, 0.5),
    0 0 0 2px rgba(255, 255, 255, 0.1),
    inset 0 0 0 2px rgba(0, 0, 0, 0.8);
  animation: phoneFloat 3s ease-in-out infinite;
}

.phone-mockup::before {
  content: '';
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 5px;
  background: #333;
  border-radius: 3px;
  z-index: 10;
}

.phone-mockup__screen {
  width: 100%;
  height: auto;
  border-radius: 26px;
  display: block;
  background: #fff;
}

.carousel__caption {
  margin-top: 16px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.5;
  max-width: 300px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  padding: 0 10px;
}

/* Navigation Buttons */
.carousel__btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.95);
  color: #2A9D9B;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  z-index: 10;
}

.carousel__btn:hover:not(:disabled) {
  background: #fff;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
}

.carousel__btn:active:not(:disabled) {
  transform: translateY(-50%) scale(0.95);
}

.carousel__btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.carousel__btn--prev {
  left: 0;
}

.carousel__btn--next {
  right: 0;
}

.carousel__dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
}

.carousel__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.carousel__dot:hover {
  background: rgba(255, 255, 255, 0.6);
  transform: scale(1.2);
}

.carousel__dot--active {
  background: rgba(255, 255, 255, 0.95);
  width: 12px;
  height: 12px;
}

@keyframes phoneFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Contact Form Section */
.contact-form-section {
  max-width: 700px;
  margin: 40px auto;
  padding: 40px 24px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  text-align: center;
}

.contact-form-section .section__title {
  background: linear-gradient(135deg, #40C4C2 0%, #2A9D9B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: none;
}

.contact-form__description {
  margin: 12px 0 28px;
  font-size: 16px;
  color: #4a5568;
  line-height: 1.7;
  padding: 0 10px;
}

.contact-form {
  text-align: right;
  dir: rtl;
}

.contact-form__field {
  margin-bottom: 20px;
}

.contact-form__input,
.contact-form__textarea {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid rgba(64, 196, 194, 0.2);
  border-radius: 12px;
  font-size: 16px;
  background: #fff;
  transition: all 0.3s ease;
}

.contact-form__input:focus,
.contact-form__textarea:focus {
  outline: none;
  border-color: #40C4C2;
  box-shadow: 0 0 0 3px rgba(64, 196, 194, 0.1);
}

.contact-form__textarea {
  resize: vertical;
  min-height: 120px;
}

.contact-form__field--checkbox {
  margin-bottom: 20px;
  text-align: right;
}

.contact-form__checkbox-label {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
  direction: rtl;
}

.contact-form__checkbox-input {
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  margin: 0;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 2px;
}

.contact-form__checkbox-text {
  display: inline;
}

.contact-form__privacy-link {
  color: #2A9D9B;
  text-decoration: underline;
  transition: color 0.3s ease;
  display: inline;
  pointer-events: auto;
}

.contact-form__privacy-link:hover {
  color: #40C4C2;
  text-decoration: underline;
}

.contact-form__submit {
  text-align: center;
  margin-top: 24px;
}

.contact-form__button {
  background: linear-gradient(135deg, #40C4C2 0%, #2A9D9B 100%);
  border: none;
  border-radius: 50px;
  padding: 16px 40px;
  font-size: 17px;
  font-weight: 800;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 12px 32px rgba(64, 196, 194, 0.5);
  transition: all 0.3s ease;
  min-width: 200px;
}

.contact-form__button:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 20px 50px rgba(64, 196, 194, 0.7);
}

.contact-form__button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.contact-form__message {
  margin-top: 20px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
}

.contact-form__message.success {
  background: rgba(64, 196, 194, 0.1);
  color: #2A9D9B;
  border: 2px solid rgba(64, 196, 194, 0.3);
}

.contact-form__message.error {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: 2px solid rgba(220, 53, 69, 0.3);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

/* CTA Section */
.cta {
  max-width: 700px;
  margin: 40px 20px;
  text-align: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  border-radius: 24px;
  padding: 40px 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  position: relative;
  overflow: hidden;
}

.cta::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(64, 196, 194, 0.15) 0%, transparent 70%);
  animation: rotate 20s linear infinite;
}

.cta > * {
  position: relative;
  z-index: 1;
}

.cta .section__title {
  color: #2d3748;
  font-size: 24px;
  text-shadow: none;
}

.cta__lead {
  margin: 12px 0 28px;
  font-size: 16px;
  color: #4a5568;
  line-height: 1.7;
  padding: 0 10px;
}

.cta__buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.cta__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  border-radius: 50px;
  padding: 16px 40px;
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(135deg, #40C4C2 0%, #2A9D9B 100%);
  color: #fff;
  box-shadow: 0 12px 32px rgba(64, 196, 194, 0.5);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  touch-action: manipulation;
  min-width: 200px;
}

.cta__whatsapp-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.cta__button--secondary {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  color: #2A9D9B;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
}

.cta__button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.cta__button:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 50px rgba(64, 196, 194, 0.7);
}

.cta__button--secondary:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, #fff 0%, rgba(255, 255, 255, 0.95) 100%);
}

.cta__button:hover::before {
  left: 100%;
}

.cta__button:active {
  transform: translateY(-1px);
}

.cta__button:focus {
  outline: 3px solid rgba(64, 196, 194, 0.5);
  outline-offset: 4px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 767px) {
  .intro__flow {
    flex-direction: column;
    gap: 4px;
    align-items: center;
  }

  .intro__flow-arrow {
    margin: 4px 0;
    font-size: 0;
  }

  .intro__flow-arrow::before {
    content: '⬇';
    font-size: 16px;
    display: inline-block;
  }

  .intro__flow-segment {
    padding: 4px 0;
    text-align: center;
  }

  .demo {
    margin: 40px 24px;
    padding: 32px 16px;
  }

  .contact-form-section {
    margin: 40px 24px;
    padding: 40px 16px;
  }

  .showcase {
    margin: 40px 16px;
    padding: 32px 12px;
  }

  .showcase__subtitle {
    font-size: 14px;
    margin: 10px 0 24px;
  }

  .carousel {
    padding: 0 40px;
    max-width: 100%;
  }

  .carousel__btn {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  .carousel__btn--prev {
    left: 5px;
  }

  .carousel__btn--next {
    right: 5px;
  }

  .phone-mockup {
    max-width: 220px;
  }

  .carousel__caption {
    font-size: 13px;
    margin-top: 16px;
  }

  .hero__whatsapp-button {
    padding: 12px 28px;
    font-size: 16px;
  }

  .section__whatsapp-button {
    padding: 12px 28px;
    font-size: 15px;
  }

  .section__cta {
    margin: 28px auto 0;
  }
}

@media (min-width: 768px) {
  .store {
    padding-top: 72px;
  }

  .store-header__container {
    padding: 16px 24px;
  }

  .store-header__logo img {
    height: 40px;
  }

  .store-header__cta {
    padding: 10px 24px;
    font-size: 16px;
  }

  .hero {
    padding: 80px 40px 100px;
  }

  .hero__logo img {
    height: 80px;
  }

  .hero__title {
    font-size: 42px;
    padding: 0;
  }
  
  .hero__subtitle {
    font-size: 20px;
    padding: 0;
    margin-bottom: 30px;
  }

  .hero__note {
    font-size: 18px;
    padding: 14px 24px;
    margin-bottom: 30px;
  }

  .hero__highlight {
    padding: 24px;
  }

  .hero__highlight-text {
    font-size: 24px;
  }

  .hero__description {
    font-size: 18px;
    padding: 0;
  }

  .hero__cta {
    margin: 40px auto 0;
  }

  .hero__whatsapp-button {
    padding: 16px 36px;
    font-size: 18px;
  }
  
  .section__title {
    font-size: 28px;
    padding: 0;
  }

  .section__cta {
    margin: 40px auto 0;
  }

  .section__whatsapp-button {
    padding: 16px 36px;
    font-size: 17px;
  }

  .intro {
    padding: 80px 24px;
  }

  .intro__text {
    font-size: 18px;
  }

  .intro__flow {
    font-size: 18px;
  }

  .intro__flow-arrow {
    font-size: 20px;
  }

  .intro__text--highlight {
    font-size: 20px;
  }

  .customer-gets {
    padding: 60px 24px;
  }

  .customer-gets__emphasis {
    font-size: 20px;
  }

  .customer-gets__text {
    font-size: 17px;
  }

  .customer-gets__conclusion {
    font-size: 18px;
  }

  .how-it-works {
    padding: 60px 24px;
  }

  .steps__note {
    font-size: 17px;
  }

  .steps {
    gap: 28px;
  }

  .step {
    padding: 28px;
  }

  .step__number {
    width: 56px;
    height: 56px;
    font-size: 24px;
  }

  .step__title {
    font-size: 20px;
  }

  .step__description {
    font-size: 17px;
  }
  
  .benefits {
    margin: 0 auto;
    padding: 60px 40px;
  }

  .benefits__list {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .benefits__item {
    padding: 24px;
  }

  .benefits__icon {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }

  .benefits__text {
    font-size: 17px;
  }

  .benefits__text strong {
    font-size: 18px;
  }

  .pricing {
    padding: 60px 24px;
  }

  .pricing__simple {
    gap: 24px;
  }

  .pricing__simple-item {
    padding: 28px 24px;
    font-size: 20px;
  }

  .demo {
    padding: 48px 32px;
  }

  .demo__example-title {
    font-size: 22px;
  }

  .demo__example-description {
    font-size: 18px;
  }

  .demo__link {
    font-size: 16px;
  }

  .showcase {
    padding: 60px 24px;
  }

  .showcase__subtitle {
    font-size: 16px;
    margin: 16px 0 40px;
  }

  .carousel {
    max-width: 460px;
    padding: 0 52px;
  }

  .phone-mockup {
    max-width: 270px;
  }

  .carousel__caption {
    font-size: 15px;
    margin-top: 24px;
  }

  .contact-form-section {
    padding: 60px 40px;
    margin: 60px auto;
  }

  .contact-form__description {
    font-size: 18px;
  }

  .contact-form__input,
  .contact-form__textarea {
    font-size: 17px;
    padding: 16px 20px;
  }

  .contact-form__button {
    padding: 18px 48px;
    font-size: 18px;
    min-width: 220px;
  }

  .cta {
    margin: 60px auto;
    padding: 60px 40px;
  }

  .cta .section__title {
    font-size: 32px;
  }

  .cta__lead {
    font-size: 19px;
  }

  .cta__buttons {
    flex-direction: row;
    gap: 20px;
    justify-content: center;
  }

  .cta__button {
    padding: 18px 48px;
    font-size: 18px;
    min-width: 220px;
  }
}

@media (min-width: 1024px) {
  .store {
    padding-top: 82px;
  }

  .hero {
    padding: 120px 40px 120px;
  }

  .hero__title {
    font-size: 58px;
  }
  
  .hero__logo img {
    height: 100px;
  }

  .hero__highlight-text {
    font-size: 28px;
  }

  .hero__note {
    font-size: 19px;
    padding: 16px 28px;
  }

  .hero__description {
    margin-bottom: 40px;
  }

  .intro__flow {
    font-size: 19px;
  }

  .intro__flow-arrow {
    font-size: 22px;
  }
 
  .customer-gets__emphasis {
    font-size: 22px;
  }

  .customer-gets__conclusion {
    font-size: 19px;
  }
  
  .benefits {
    padding: 80px 60px;
  }

  .pricing {
    padding: 80px 60px;
  }

  .pricing__simple-item {
    font-size: 22px;
    padding: 32px 28px;
  }

  .demo {
    padding: 60px 40px;
  }

  .demo__example {
    margin-bottom: 50px;
  }

  .demo__example-title {
    font-size: 24px;
  }

  .demo__example-description {
    font-size: 19px;
  }

  .showcase {
    padding: 80px 40px;
  }

  .showcase__subtitle {
    font-size: 17px;
    margin: 16px 0 48px;
  }

  .carousel {
    max-width: 500px;
  }

  .phone-mockup {
    max-width: 300px;
  }

  .carousel__caption {
    font-size: 16px;
  }

  .store-header__logo img {
    height: 50px;
  }
}

/* Footer Styles */
.store-footer {
  background: #061c18;
  color: rgba(255, 255, 255, 0.9);
  padding: 40px 20px 20px;
}

.store-footer__container {
  max-width: 1200px;
  margin: 0 auto;
}

.store-footer__content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  margin-bottom: 28px;
  text-align: right;
}

.store-footer__brand {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.store-footer__logo {
  height: 35px;
  width: auto;
}

.store-footer__tagline {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

.store-footer__title {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 10px;
  color: #40C4C2;
}

.store-footer__link {
  display: block;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 8px;
  transition: color 0.3s ease;
  padding: 4px 0;
}

.store-footer__link:hover {
  color: #40C4C2;
}

.store-footer__social-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s ease;
  padding: 4px 0;
}

.store-footer__social-link:hover {
  color: #40C4C2;
}

.store-footer__bottom {
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.store-footer__copyright {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

@media (min-width: 768px) {
  .store-footer__content {
    grid-template-columns: 2fr 1fr 1fr 1fr;
    text-align: right;
  }

  .store-footer__logo {
    height: 50px;
  }
}

/* Store planners: light theme overrides */
.store-planners main {
  color: #1e1e1e;
}

.store-planners .section__title {
  color: #1e1e1e !important;
  text-shadow: none !important;
}

.store-planners .showcase__subtitle,
.store-planners .intro__text,
.store-planners .intro__flow,
.store-planners .step__description,
.store-planners .demo__example-description,
.store-planners .customer-gets__conclusion {
  color: rgba(33, 37, 41, 0.82) !important;
  text-shadow: none !important;
}

.store-planners .carousel__caption {
  color: rgba(33, 37, 41, 0.82) !important;
  text-shadow: none !important;
}

.store-planners .customer-gets__emphasis,
.store-planners .customer-gets__text,
.store-planners .benefits__text,
.store-planners .benefits__text strong,
.store-planners .step__title,
.store-planners .demo__example-title,
.store-planners .steps__note {
  color: #1e1e1e !important;
  text-shadow: none !important;
}

.store-planners .showcase {
  background-color: #E6F5F9;
  border-radius: 32px;
}

.store-planners .customer-gets,
.store-planners .intro,
.store-planners .how-it-works,
.store-planners .benefits,
.store-planners .demo,
.store-planners .cta {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 32px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
}

.store-planners .customer-gets__highlight-box,
.store-planners .customer-gets__conclusion,
.store-planners .intro__text--highlight,
.store-planners .steps__note {
  background: rgba(7, 160, 195, 0.08);
  border: 1px solid rgba(7, 160, 195, 0.12);
}

.store-planners .customer-gets__item,
.store-planners .benefits__item,
.store-planners .step {
  background: rgba(7, 160, 195, 0.06);
  border-color: rgba(7, 160, 195, 0.14);
}

.store-planners .customer-gets__icon,
.store-planners .benefits__icon,
.store-planners .step__number {
  color: #fff;
}

.store-planners .demo {
  text-align: center;
}

.store-planners .demo__link-container {
  background: rgba(7, 160, 195, 0.06);
  border: 1px solid rgba(7, 160, 195, 0.14);
}

.store-planners .demo__link {
  color: #07A0C3;
}

.store-planners .demo__link:hover {
  color: #07A0C3;
}
</style>

